// ── 메시지 영역 ───────────────────────────────────
// 스크롤 컨테이너 + 메시지 버블(사용자/어시스턴트) + 날짜 구분선 + 첨부 칩 + 복사/저장/재생성 +
// RAG 출처 카드 + 각종 진행중 표시(답변·문서생성·도구) + 맨아래 이동 버튼.
// 순수 프레젠테이션: 상태·핸들러·ref는 ChatPage에서 props로 주입(동작 100% 동일).
// 본문 렌더는 renderMessage(rawContent) prop으로 위임(ChatPage→MessageContent).
import { useEffect, useRef, useState } from 'react';
import { kstDateKey, fmtMsgDate, fmtMsgTime } from '../../utils/msgDate';
import { stripDocMarkers } from '../../utils/docMarkers';
import Markdown from '../../components/Markdown';
import MsgCopyButton from './MsgCopyButton';
import { clearAllAttachmentReuseFiles, clearAttachmentReuseFiles, getAttachmentReuseFiles, setAttachmentReuseFiles, subscribeAttachmentReuseFiles } from './hooks/useChatMessage';
import { IconDownload, IconFileText, IconFolder, IconPaperclip, IconPencil, IconRefresh, IconSparkles, IconTrash } from '@tabler/icons-react';
import { BOT_NAME_FULL, BOT_ORIGIN } from '../../utils/brand';
import { PageHeader } from '../../components/ui';

const loadingIndicatorStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 12px',
  background: '#f8fafc',
  border: '1px solid var(--border)',
  borderRadius: '999px',
  color: 'var(--text-muted)',
  fontSize: '13px',
  fontWeight: '500',
  lineHeight: 1.4,
};

const loadingSpinnerStyle = {
  width: '12px',
  height: '12px',
  border: '2px solid var(--accent-weak)',
  borderTopColor: 'var(--accent)',
  borderRadius: '50%',
  display: 'inline-block',
  flexShrink: 0,
  animation: 'docspin 0.7s linear infinite',
};

function LoadingIndicator({ label, mobile, withAvatar = false }) {
  return (
    <div aria-live="polite" aria-busy="true" style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, marginLeft: withAvatar || mobile ? '0' : '36px' }}>
      {withAvatar && (
        <div style={{ width: '28px', height: '28px', background: 'var(--accent)', borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <IconSparkles size={14} stroke={2} color="white" aria-hidden="true" />
        </div>
      )}
      <div style={loadingIndicatorStyle}>
        <span style={loadingSpinnerStyle} />
        {label}
      </div>
    </div>
  );
}

function ThinkingBlock({ text }) {
  if (!text) return null;
  return (
    <details style={{ margin: '0 0 12px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 10px', color: '#4b5563', fontFamily: 'var(--gw-font)' }}>
      <summary style={{ cursor: 'pointer', fontSize: '12px', fontWeight: 700, color: '#6b7280', lineHeight: 1.4 }}>
        사고 과정
      </summary>
      <div style={{ marginTop: '8px', fontSize: '12px', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        <Markdown compact muted>{text}</Markdown>
      </div>
    </details>
  );
}

const citationList = (message) => (
  Array.isArray(message?.citations)
    ? message.citations.filter((c) => (c?.cited_text || '').trim())
    : []
);

const citationLocation = (citation) => {
  const parts = [];
  if (citation.page !== null && citation.page !== undefined) parts.push(`p.${citation.page}`);
  if (citation.sheet) parts.push(`sheet ${citation.sheet}`);
  if (citation.slide !== null && citation.slide !== undefined) parts.push(`slide ${citation.slide}`);
  if (citation.start !== null && citation.start !== undefined && citation.end !== null && citation.end !== undefined) {
    parts.push(`${citation.start}-${citation.end}`);
  }
  return parts.join(' · ');
};

const sourceLabel = (sourceType) => {
  if (sourceType === 'web') return '웹';
  if (sourceType === 'project_rag') return '프로젝트 문서';
  if (sourceType === 'rag') return '사내 문서';
  return sourceType || '출처';
};

const groupedCitations = (message) => {
  const groups = [];
  const byKey = new Map();
  citationList(message).forEach((citation) => {
    const key = [
      citation.source_type || '',
      citation.title || '',
      citation.url || '',
      citation.document_id ?? '',
      citation.chunk_id ?? '',
    ].join('|');
    let group = byKey.get(key);
    if (!group) {
      group = {
        source_type: citation.source_type,
        title: citation.title,
        url: citation.url,
        document_id: citation.document_id,
        chunk_id: citation.chunk_id,
        source_ref: citation.source_ref || {},
        citations: [],
      };
      byKey.set(key, group);
      groups.push(group);
    }
    group.citations.push(citation);
  });
  return groups;
};

const markerDocs = (content) => {
  const docs = new Map();
  for (const match of (content || '').matchAll(/\[\[doc:([^:\]]+):([^\]]+)\]\]/g)) {
    const [, id, rawMeta] = match;
    const [name, ...metaParts] = rawMeta.split('|');
    const next = { ...(docs.get(id) || {}), id, name: name || 'generated-file' };
    metaParts.forEach((part) => {
      const eq = part.indexOf('=');
      if (eq <= 0) return;
      const key = part.slice(0, eq);
      const value = part.slice(eq + 1);
      if (key === 'mime') next.mime_type = value;
      else if (key === 'size') {
        const n = Number(value);
        if (Number.isFinite(n)) next.size = n;
      } else if (key === 'format') next.format = value;
    });
    docs.set(id, next);
  }
  return docs;
};

const stripGeneratedMarkers = (content) =>
  (content || '').replace(/\[\[doc:[^\]]+\]\]/g, '').replace(/\n{3,}/g, '\n\n').replace(/\s+$/, '');

const fileFormatLabel = (file) => {
  const format = (file?.format || '').toString().trim();
  if (format) return format.toUpperCase();
  const ext = (file?.name || '').split('.').pop();
  return ext && ext !== file?.name ? ext.toUpperCase() : '파일';
};

const formatGeneratedFileSize = (size) => {
  const n = Number(size);
  if (!Number.isFinite(n) || n < 0) return '크기 확인 전';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10 * 1024 ? 1 : 0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// 턴 상태·종료 사유를 사람 말로. 내부 값을 그대로 보여주면 사용자는 뜻을 모른다.
const STATUS_LABELS = {
  running: '응답 못 받음',      // 끊겨서 굳은 턴(리퍼가 정리하기 전) — 사용자 눈엔 '아직 만드는 중'으로 오해된다
  failed: '응답 실패',
  cancelled: '중지됨',
  aborted: '중지됨',
  paused: '일시 중지',
  exhausted: '반복 한도 도달',
};
const REASON_LABELS = {
  abandoned: '연결이 끊겨 중단',
  client_abort: '중지됨',
  max_tokens: '길이 제한으로 잘림',
  pause_turn: '일시 중지',
};
// 상태 배지가 이미 그 뜻을 담고 있어 중복인 사유들
const REASON_COVERED_BY_STATUS = new Set(['client_abort', 'abandoned', 'pause_turn']);

const turnSnapshotBadges = (message) => {
  const snapshot = message?.turn_snapshot || message?.execution_snapshot;
  if (!snapshot || typeof snapshot !== 'object') return [];
  const settings = snapshot.settings || snapshot.execution || {};
  const badges = [];
  const model = snapshot.model_id || snapshot.model?.actual || snapshot.model?.requested;
  if (model) badges.push(`모델 ${model}`);
  if (settings.reasoning) badges.push('추론');
  if (settings.web_search) badges.push('웹');
  if (settings.rag || settings.use_rag) badges.push('RAG');
  if (settings.cowork) badges.push('Cowork');
  if (settings.internal_tool || settings.internal_data) badges.push('사내 도구');
  const documentTool = settings.document_tool;
  if ((typeof documentTool === 'object' && documentTool?.enabled) || documentTool === true) badges.push('문서 도구');
  // 🔴 예전엔 내부 영어 상태값을 그대로 노출했다(`상태 running`). 사용자에겐 뜻이 안 통하고,
  //    끊겨서 굳은 턴은 그 배지가 영원히 남는다(실측: 사용자 2명이 51시간째 '상태 running').
  const statusLabel = STATUS_LABELS[snapshot.status];
  if (statusLabel) badges.push(statusLabel);
  else if (snapshot.status && snapshot.status !== 'legacy' && snapshot.status !== 'complete') badges.push(`상태 ${snapshot.status}`);
  const terminalReason = snapshot.terminal_reason || snapshot.run?.terminal_reason;
  const reason = (terminalReason && terminalReason !== snapshot.status && terminalReason !== snapshot.stop_reason)
    ? terminalReason
    : (snapshot.stop_reason && snapshot.stop_reason !== 'end_turn' ? snapshot.stop_reason : null);
  // 상태 배지가 이미 같은 뜻을 말하면 종료 사유를 겹쳐 붙이지 않는다.
  if (reason && !(statusLabel && REASON_COVERED_BY_STATUS.has(reason))) {
    badges.push(REASON_LABELS[reason] || `종료 ${reason}`);
  }
  return badges;
};

const downloadGeneratedFile = async (file) => {
  try {
    const token = localStorage.getItem('token');
    const res = await fetch(`/api/chat/generated/${encodeURIComponent(file.id)}`, { headers: token ? { Authorization: `Bearer ${token}` } : undefined });
    if (!res.ok) throw new Error('download failed');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name || 'generated-file';
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    window.alert?.('문서 다운로드에 실패했습니다.');
  }
};

function GeneratedFileCards({ files }) {
  const list = [...files.values()];
  if (!list.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px', maxWidth: '360px' }}>
      {list.map((file) => (
        <button
          key={file.id}
          type="button"
          onClick={() => downloadGeneratedFile(file)}
          style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: '#f8fafc', border: '1px solid var(--border)', borderRadius: '8px', cursor: 'pointer', width: '100%', boxSizing: 'border-box', minWidth: 0, fontFamily: 'var(--gw-font)', textAlign: 'left' }}
          title="클릭하여 문서를 다운로드합니다"
        >
          <IconFileText size={24} stroke={1.7} color="var(--accent)" aria-hidden="true" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{fileFormatLabel(file)} · {formatGeneratedFileSize(file.size)}</div>
          </div>
          <IconDownload size={17} stroke={1.8} color="var(--accent)" aria-hidden="true" style={{ flexShrink: 0 }} />
        </button>
      ))}
    </div>
  );
}

export default function MessageList({
  mobile,
  keyboardOpen = false,
  messages, loading, currentConvId,
  scrollRef, handleMessagesScroll, bottomRef,
  downloadFile, renderMessage,
  saveMenu, setSaveMenu, saveMsg, handleRegenerate,
  handleEditMessage, handleDeleteMessage,
  genConvIds, generatingDoc, workspaceBusy,
  showScrollDown, scrollToBottom,
  emptyCentered = false, emptyGreeting = '무엇을 도와드릴까요?',
  starterPrompts = [], onStarterPromptClick,
}) {
  const isEmpty = messages.length === 0;
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const convIdForReuseRef = useRef(currentConvId);
  const [reuseFiles, setReuseFiles] = useState(() => getAttachmentReuseFiles(currentConvId));
  useEffect(() => {
    if (convIdForReuseRef.current === currentConvId) return;
    clearAttachmentReuseFiles(convIdForReuseRef.current);
    convIdForReuseRef.current = currentConvId;
    setReuseFiles(getAttachmentReuseFiles(currentConvId));
  }, [currentConvId]);
  useEffect(() => {
    const unsubscribe = subscribeAttachmentReuseFiles((convId, files) => {
      if (String(convId) === String(convIdForReuseRef.current)) setReuseFiles(files);
    });
    return () => {
      unsubscribe();
      clearAllAttachmentReuseFiles();
    };
  }, []);
  const reuseFileIds = new Set(reuseFiles.map((file) => Number(file.id)));
  const isStreaming = Boolean(loading || (currentConvId && typeof genConvIds?.has === 'function' && genConvIds.has(currentConvId)));
  const actionButtonStyle = (disabled = false) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    background: 'none',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '12px',
    color: '#6b7280',
    minHeight: '28px',
    flexShrink: 0,
    opacity: disabled ? 0.55 : 1,
  });
  const startEditing = (message) => {
    if (isStreaming) return;
    setEditingMessageId(message.id);
    setEditingText(message.content === '(첨부 파일)' && message.files?.length > 0 ? '' : (message.content || ''));
  };
  const cancelEditing = () => {
    setEditingMessageId(null);
    setEditingText('');
  };
  const submitEditing = async (message) => {
    const ok = await handleEditMessage?.(message.id, editingText);
    if (ok) cancelEditing();
  };
  const toggleReuseFile = (file) => {
    const fileId = Number(file?.id);
    if (!currentConvId || !Number.isFinite(fileId) || file?.expired || file?.reusable === false) return;
    setReuseFiles((prev) => {
      const exists = prev.some((item) => Number(item.id) === fileId);
      const next = exists
        ? prev.filter((item) => Number(item.id) !== fileId)
        : [...prev, { id: fileId, name: file.name || '첨부 파일', expired: false }];
      setAttachmentReuseFiles(currentConvId, next);
      return next;
    });
  };

  return (
    <div style={{ flex: isEmpty && emptyCentered ? '0 0 auto' : 1, position: 'relative', display: 'flex', minHeight: isEmpty && emptyCentered ? undefined : 0 }}>
    {/* --gw-app-h가 키보드 높이로 줄면 이 flex 자식만 스크롤되고 composer는 하단 가시 영역에 남는다. */}
    <div ref={scrollRef} onScroll={handleMessagesScroll} style={{ flex: 1, overflowY: isEmpty && emptyCentered ? 'visible' : 'auto', padding: mobile && keyboardOpen ? '18px 0 8px' : (isEmpty && emptyCentered ? (mobile ? '8px 0 14px' : '10px 0 18px') : (mobile ? '18px 0 22px' : '32px 0 34px')), minWidth: 0 }}>
      <div style={{ maxWidth: '900px', minHeight: isEmpty && emptyCentered ? undefined : '100%', margin: '0 auto', padding: mobile ? '0 12px' : '0 20px', display: 'flex', flexDirection: 'column', gap: mobile ? '26px' : '32px', minWidth: 0, boxSizing: 'border-box' }}>
      {isEmpty && (
        <div role="region" aria-label="새 대화 시작" style={{ flex: emptyCentered ? '0 0 auto' : 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'center', color: 'var(--gw-secondary, #64635f)', textAlign: 'left', padding: emptyCentered ? (mobile ? '14px 10px 8px' : '18px 0 10px') : (mobile ? '44px 10px' : '60px 0'), minWidth: 0 }}>
          {/* 자기소개 — 새 대화 첫 화면은 챗봇이 자기 이름을 알리는 자리다(2026-08-21 OURi 개명).
              이름은 상수로 — 개명하면 여기도 따라간다. 입력창 placeholder('무엇이든 물어보세요')와 같은 말투.
              머리는 공용 PageHeader(md · 시안 F f11-ai 2a) — 크기·구간은 index.html 의 .gw-ph--md 규칙이 맡는다(인라인 크기 금지).
              부제는 <p> 라 안에는 인라인 요소만 둔다. */}
          <PageHeader
            size="md"
            icon={IconSparkles}
            title={emptyGreeting}
            subtitle={<>
              사내 AI 챗봇 <b style={{ color: 'var(--gw-text, #32312e)', fontWeight: 700 }}>{BOT_NAME_FULL}</b>입니다. 무엇이든 물어보세요.
              {/* 이름 뜻 — 처음 보는 사람이 'OURi 가 뭐지' 하고 멈추지 않게 한 줄로 알려준다(마스터 2026-08-21).
                  자기소개보다 한 단계 작게 — 읽을 사람은 읽고, 아는 사람은 지나친다. 하한 10px(≤600 부제가 10px). */}
              <span style={{ display: 'block', fontSize: 'max(0.9em, 10px)', marginTop: '3px' }}>{BOT_ORIGIN}</span>
            </>}
          />
          {starterPrompts.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start', gap: '8px', maxWidth: '560px' }}>
              {starterPrompts.map((prompt) => (
                <button key={prompt} type="button" onClick={() => onStarterPromptClick?.(prompt)}
                  style={{ border: '1px solid var(--gw-border, #e5e5e2)', background: 'var(--gw-surface, #fff)', color: 'var(--gw-secondary, #64635f)', borderRadius: '20px', padding: '5px 11px', cursor: 'pointer', fontSize: '12px', fontWeight: 500, lineHeight: '18px' }}>
                  {prompt}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {messages.map((m, i) => {
        const isLastAssistant = i === messages.length - 1 && m.role === 'assistant' && !loading && currentConvId;
        const generatedFiles = m.role === 'assistant' ? markerDocs(m.content) : new Map();
        const turnBadges = m.role === 'user' ? turnSnapshotBadges(m) : [];
        // 드라이브 폴더 첨부(이력 API·낙관적 메시지 공통) — 첨부만 보낸 턴의 플레이스홀더는 파일 첨부와 같이 숨긴다.
        const driveFolders = m.role === 'user' && Array.isArray(m.drive_folders) ? m.drive_folders : [];
        const attachmentOnly = m.content === '(첨부 파일)' && (m.files?.length > 0 || driveFolders.length > 0);
        // 날짜 구분선: 직전 메시지와 KST 날짜가 바뀌면 가운데 구분선(첫 메시지 포함). created_at 없으면 생략.
        const dkey = kstDateKey(m.created_at);
        const prevDkey = i > 0 ? kstDateKey(messages[i - 1].created_at) : '';
        const showDateDivider = dkey && dkey !== prevDkey;
        return (
          <div key={i} className="chat-message-row" title={m.created_at ? fmtMsgTime(m.created_at) : undefined}>
            {showDateDivider && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '4px 0', color: 'var(--text-muted)' }}>
                <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
                <span style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{fmtMsgDate(m.created_at)}</span>
                <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', minWidth: 0 }}>
              {m.role === 'assistant' && (
                <div style={{ width: '28px', height: '28px', background: 'var(--accent)', borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '8px', flexShrink: 0, marginTop: '2px' }}>
                  <IconSparkles size={14} stroke={2} color="white" aria-hidden="true" />
                </div>
              )}
              <div style={{
                maxWidth: m.role === 'user' ? (mobile ? '92%' : '85%') : '100%',
                flex: m.role === 'assistant' ? '1 1 auto' : '0 1 auto',
                minWidth: 0,
                padding: m.role === 'user' ? (mobile ? '10px 13px' : '13px 17px') : 0, borderRadius: m.role === 'user' ? '11px' : 0,
                background: m.role === 'user' ? 'var(--user-bg)' : 'transparent',
                color: 'var(--text)',
                boxShadow: 'none',
                fontSize: '14px', lineHeight: m.role === 'user' ? '1.75' : '1.8',
                overflowWrap: 'anywhere',
                ...(m.role === 'user' ? { whiteSpace: 'pre-wrap', wordBreak: 'break-word' } : {}),
              }}>
                {m.role === 'user' ? (
                  <>
                    {editingMessageId === m.id ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: mobile ? '220px' : '320px', maxWidth: '100%' }}>
                        <textarea
                          value={editingText}
                          onChange={(e) => setEditingText(e.target.value)}
                          onKeyDown={(e) => {
                            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') submitEditing(m);
                            if (e.key === 'Escape') cancelEditing();
                          }}
                          autoFocus
                          rows={Math.min(8, Math.max(2, editingText.split('\n').length))}
                          disabled={isStreaming}
                          style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', resize: 'vertical', border: '1px solid var(--border)', borderRadius: '8px', padding: '8px 10px', background: 'white', color: 'var(--text)', font: 'inherit', fontSize: '14px', lineHeight: 1.5, outline: 'none' }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px', flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            disabled={isStreaming || (!editingText.trim() && !(m.files?.length > 0))}
                            onClick={() => submitEditing(m)}
                            style={actionButtonStyle(isStreaming || (!editingText.trim() && !(m.files?.length > 0)))}
                          >
                            저장
                          </button>
                          <button type="button" disabled={isStreaming} onClick={cancelEditing} style={actionButtonStyle(isStreaming)}>
                            취소
                          </button>
                        </div>
                      </div>
                      ) : (
                        !attachmentOnly && m.content
                      )}
                      {turnBadges.length > 0 && (
                        <div aria-label="턴 실행 설정" style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: (editingMessageId === m.id || (m.content && !attachmentOnly)) ? '8px' : 0 }}>
                          {turnBadges.map((badge) => (
                            <span key={badge} style={{ display: 'inline-flex', alignItems: 'center', minHeight: '22px', padding: '2px 7px', borderRadius: '7px', border: '1px solid var(--border)', background: 'rgba(255,255,255,.72)', color: 'var(--text-muted)', fontSize: '11px', fontWeight: 700, lineHeight: 1.35 }}>
                              {badge}
                            </span>
                          ))}
                        </div>
                      )}
                      {driveFolders.length > 0 && (
                        <div aria-label="첨부한 드라이브 폴더" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: (editingMessageId === m.id || (m.content && !attachmentOnly)) ? '8px' : 0 }}>
                          {driveFolders.map((f, fi) => (
                            <span key={f.path || fi} title={`드라이브 ${f.path} — 하위 폴더까지 AI가 읽습니다`}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: 'white', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '8px', padding: '5px 9px', fontSize: '12px', minWidth: 0, maxWidth: mobile ? '100%' : '300px' }}>
                              <IconFolder size={14} stroke={1.8} aria-hidden="true" style={{ flexShrink: 0 }} />
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{`${f.name || f.path} · 파일 ${f.file_count ?? 0}${f.truncated ? '+' : ''}`}</span>
                            </span>
                          ))}
                        </div>
                      )}
                      {m.files?.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: driveFolders.length > 0 ? '6px' : ((editingMessageId === m.id || (m.content && !attachmentOnly)) ? '8px' : 0) }}>
                          {m.files.map((f) => (
                            <div key={f.id} style={{ display: 'inline-flex', alignItems: 'stretch', maxWidth: mobile ? '100%' : '300px', minWidth: 0 }}>
                              <button onClick={() => downloadFile(f)}
                                title={f.expired ? '보관 기간(30일)이 지나 다운로드할 수 없습니다' : '클릭하면 다운로드'}
                                style={{ display: 'flex', alignItems: 'center', gap: '5px', background: 'white', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: '8px 0 0 8px', padding: '5px 9px', cursor: f.expired ? 'not-allowed' : 'pointer', fontSize: '12px', opacity: f.expired ? 0.55 : 1, minWidth: 0, maxWidth: mobile ? 'calc(100% - 82px)' : '220px' }}>
                                <IconPaperclip size={14} stroke={1.8} aria-hidden="true" style={{ flexShrink: 0 }} />
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                                <span style={{ fontSize: '11px', flexShrink: 0 }}>{f.expired ? '(만료)' : <IconDownload size={13} stroke={1.8} aria-hidden="true" />}</span>
                              </button>
                              <button
                                type="button"
                                aria-pressed={reuseFileIds.has(Number(f.id))}
                                disabled={!currentConvId || f.expired || f.reusable === false}
                                onClick={() => toggleReuseFile(f)}
                                title={f.expired || f.reusable === false ? '원문을 다시 사용할 수 없습니다' : '후속 메시지에 다시 첨부합니다'}
                                style={{ border: '1px solid var(--border)', borderLeft: 0, borderRadius: '0 8px 8px 0', background: reuseFileIds.has(Number(f.id)) ? 'var(--accent-weak)' : 'white', color: reuseFileIds.has(Number(f.id)) ? 'var(--selected-text)' : 'var(--text-muted)', cursor: (!currentConvId || f.expired || f.reusable === false) ? 'not-allowed' : 'pointer', padding: '5px 8px', fontSize: '11px', fontWeight: 800, opacity: (!currentConvId || f.expired || f.reusable === false) ? 0.55 : 1, whiteSpace: 'nowrap', flexShrink: 0 }}
                              >
                                {reuseFileIds.has(Number(f.id)) ? '재사용 선택됨' : '재사용'}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                  </>
                ) : (
                  <>
                    <ThinkingBlock text={m.thinking} />
                    {renderMessage(stripGeneratedMarkers(m.content))}
                    <GeneratedFileCards files={generatedFiles} />
                  </>
                )}
              </div>
            </div>
            {m.role === 'user' && m.created_at && (
              <div className="chat-message-time" style={{ textAlign: 'right', fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px', marginRight: '2px' }}>
                {fmtMsgTime(m.created_at)}
              </div>
            )}
            {m.role === 'user' && m.id && (
              <div className={`chat-message-actions${editingMessageId === m.id ? ' chat-message-actions-visible' : ''}`} style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px', rowGap: '6px', marginTop: '8px', position: 'relative', flexWrap: 'wrap', maxWidth: '100%' }}>
                <button
                  type="button"
                  title="메시지 편집"
                  disabled={isStreaming || editingMessageId === m.id}
                  onClick={() => startEditing(m)}
                  style={actionButtonStyle(isStreaming || editingMessageId === m.id)}
                >
                  <IconPencil size={14} stroke={1.8} aria-hidden="true" />편집
                </button>
                <button
                  type="button"
                  title="메시지 삭제"
                  disabled={isStreaming}
                  onClick={() => handleDeleteMessage?.(m.id)}
                  style={actionButtonStyle(isStreaming)}
                >
                  <IconTrash size={14} stroke={1.8} aria-hidden="true" />삭제
                </button>
              </div>
            )}
            {m.role === 'assistant' && m.content && m.created_at && (
              <div className="chat-message-time" style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px', marginLeft: '36px' }}>
                {fmtMsgTime(m.created_at)}
              </div>
            )}
            {m.role === 'assistant' && m.content && (
              <div className={`chat-message-actions${isLastAssistant ? ' chat-message-actions-visible' : ''}`} style={{ display: 'flex', gap: '6px', rowGap: '6px', marginTop: '8px', marginLeft: '36px', position: 'relative', flexWrap: 'wrap', maxWidth: 'calc(100% - 36px)' }}>
                <MsgCopyButton text={stripDocMarkers(m.content)} />
                <button onClick={() => setSaveMenu(saveMenu === i ? null : i)}
                  style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px', background: 'none', border: '1px solid var(--border)', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', color: 'var(--text-muted)', minHeight: '28px' }}>
                  <IconDownload size={14} stroke={1.8} aria-hidden="true" />저장 ▾
                </button>
                {saveMenu === i && (
                  <div style={{ position: 'absolute', top: '100%', left: mobile ? 0 : '64px', marginTop: '4px', background: 'white', border: '1px solid var(--border)', borderRadius: '8px', boxShadow: '0 6px 20px rgba(0,0,0,.12)', zIndex: 60, overflow: 'hidden', minWidth: '120px' }}>
                    {[['txt', 'TXT (텍스트)'], ['md', 'MD (마크다운)'], ['docx', 'DOCX (워드)'], ['pdf', 'PDF']].map(([f, label]) => (
                      <button key={f} onClick={() => saveMsg(stripDocMarkers(m.content), f, null, m.citations || [])}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#374151' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}>
                        {label}
                      </button>
                    ))}
                  </div>
                )}
                {isLastAssistant && (
                  <button onClick={handleRegenerate}
                    style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px', background: 'none', border: '1px solid var(--border)', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', color: 'var(--text-muted)', minHeight: '28px' }}>
                    <IconRefresh size={14} stroke={1.8} aria-hidden="true" />재생성
                  </button>
                )}
              </div>
            )}
            {m.role === 'assistant' && groupedCitations(m).length > 0 && (
              <div style={{ marginLeft: '36px', marginTop: '10px', maxWidth: 'calc(100% - 36px)', background: '#f8fafc', border: '1px solid var(--border)', borderRadius: '10px', padding: '10px 12px', boxSizing: 'border-box' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--selected-text)', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '5px' }}><IconFileText size={14} stroke={1.8} color="var(--accent)" aria-hidden="true" />참고한 출처 ({groupedCitations(m).length})</div>
                {groupedCitations(m).map((s, si) => {
                  const citations = s.citations;
                  const heading = s.source_ref?.heading;
                  const location = citationLocation(citations[0] || {});
                  return (
                    <div key={si} style={{ fontSize: '12px', color: '#374151', padding: '3px 0', minWidth: 0 }}>
                      <div style={{ display: 'flex', gap: '6px', minWidth: 0 }}>
                        <span style={{ color: 'var(--accent)', fontWeight: 700, flexShrink: 0 }}>[{si + 1}]</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                          {s.title || s.url || '(제목 없음)'}{heading ? ` — ${heading}` : ''}
                          <span style={{ color: '#9ca3af' }}> · {sourceLabel(s.source_type)}</span>
                          {location ? <span style={{ color: '#9ca3af' }}> · {location}</span> : null}
                        </span>
                      </div>
                      {s.url && (
                        <div style={{ marginLeft: '22px', color: '#6b7280', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {s.url}
                        </div>
                      )}
                      {citations.length > 0 && (
                        <details style={{ marginTop: '5px', marginLeft: '22px' }}>
                          <summary style={{ cursor: 'pointer', color: '#6b7280', fontSize: '11px', lineHeight: 1.4 }}>
                            인용 발췌 {citations.length}개
                          </summary>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: '5px' }}>
                            {citations.map((c, ci) => (
                              <div key={ci} style={{ color: '#4b5563', background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '7px 8px', lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {c.cited_text}
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      {loading && (messages.length === 0 || messages[messages.length - 1].role !== 'assistant') && (
        <LoadingIndicator label="답변 생성 중" mobile={mobile} withAvatar />
      )}
      {genConvIds.has(currentConvId) && (
        <LoadingIndicator label="답변 생성 중" mobile={mobile} />
      )}
      {generatingDoc && (
        /* 문자열이면 그게 곧 라벨(발표자료 등 오래 걸리는 작업), true 면 기본 문구 */
        <LoadingIndicator label={typeof generatingDoc === 'string' ? generatingDoc : '문서 생성 중'} mobile={mobile} />
      )}
      {workspaceBusy && (
        /* workspaceBusy 가 문자열이면 그게 곧 라벨(어떤 도구를 쓰는 중인지), true 면 기본 문구 */
        <LoadingIndicator label={typeof workspaceBusy === 'string' ? workspaceBusy : '도구 사용 중'} mobile={mobile} />
      )}
      <div ref={bottomRef} />
      </div>
    </div>
    {showScrollDown && (
      <button onClick={scrollToBottom} title="맨 아래로 이동" aria-label="맨 아래로 이동"
        style={{ position: 'absolute', bottom: mobile ? '12px' : '18px', right: mobile ? '14px' : 'max(24px, calc((100% - 900px) / 2 + 20px))', width: '40px', height: '40px', borderRadius: '50%', background: 'white', color: '#4b5563', border: '1px solid var(--border)', boxShadow: '0 4px 14px rgba(15,23,42,.10)', cursor: 'pointer', fontSize: '20px', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 6 }}>
        ↓
      </button>
    )}
    </div>
  );
}
